import {
  ContractGenerationAnswerOperator,
  ContractGenerationDraftabilityReason,
  ContractGenerationQuestionType,
  type ContractGenerationTemplateMatchAnswer,
  type ContractGenerationTemplateDraftability,
  type ContractGenerationTemplateQuestionCreateRequest,
  type ContractGenerationTemplateQuestionInDb,
  type ContractGenerationVariantAnswerInDb,
  type ContractGenerationVariantAnswerUpdate,
  type ContractGenerationVariantAnswersSummary,
} from '@/api';
import { formatDateInputDisplay } from '@/utils/dateInput';

/**
 * The words for a template's questions — the vocabulary every questions screen
 * reads from, so the same answer is described the same way whether it is shown
 * on a variant row, in the answer editor, or beside a match candidate.
 *
 * Two halves that are deliberately asymmetric. A **variant** answers with a
 * comparison: `is 50,000`, `is under 50,000`, `is between 50,000 and 100,000`.
 * The person **drafting** answers with a plain value, and the backend keeps
 * every variant whose comparison that value satisfies. The operator belongs to
 * the variant's answer and never reaches the drafting form, which is what stops
 * a growing operator set from ever complicating it.
 *
 * Nothing here parses backend prose: the records are exhaustive, so anything
 * added upstream breaks the build rather than rendering blank.
 */

/** What the template's owner has to do about it. */
export const DRAFTABILITY_FIX: Record<ContractGenerationDraftabilityReason, string> = {
  [ContractGenerationDraftabilityReason.NO_VARIANTS]:
    'Upload at least one document for this template.',
  [ContractGenerationDraftabilityReason.UNANSWERED_REQUIRED_QUESTIONS]:
    'A question every document must answer is still unanswered by some of them.',
};

/** The short form, for a chip or a row beside the count. */
export const DRAFTABILITY_SHORT: Record<ContractGenerationDraftabilityReason, string> = {
  [ContractGenerationDraftabilityReason.NO_VARIANTS]: 'No documents',
  [ContractGenerationDraftabilityReason.UNANSWERED_REQUIRED_QUESTIONS]: 'Unanswered questions',
};

/**
 * What to tell someone who is trying to draft, as opposed to the author.
 *
 * They cannot fix any of it — the template is somebody else's to finish — so
 * the reasons collapse into one sentence that says what is wrong and points at
 * the person who can, rather than handing over a checklist they have no way of
 * working through. Null when the template is fine.
 */
export function draftBlockedMessage(
  draftability: ContractGenerationTemplateDraftability | null
): string | null {
  if (!draftability || draftability.is_draftable) return null;
  const reasons = draftability.reasons ?? [];
  if (reasons.includes(ContractGenerationDraftabilityReason.NO_VARIANTS)) {
    return 'This template has no document behind it yet, so there is nothing to draft from.';
  }
  if (reasons.includes(ContractGenerationDraftabilityReason.UNANSWERED_REQUIRED_QUESTIONS)) {
    return 'Some of this template’s versions haven’t answered every required question, so the right one can’t be worked out yet.';
  }
  return 'This template isn’t ready to draft from yet.';
}

export const QUESTION_TYPE_LABEL: Record<ContractGenerationQuestionType, string> = {
  [ContractGenerationQuestionType.SELECTION]: 'Choice',
  [ContractGenerationQuestionType.NUMBER]: 'Number',
  [ContractGenerationQuestionType.DATE]: 'Date',
};

/** What each type asks of a document, for the editor's explanatory line. */
export const QUESTION_TYPE_HINT: Record<ContractGenerationQuestionType, string> = {
  [ContractGenerationQuestionType.SELECTION]:
    'A word each document serves. The list of answers builds itself from what they say.',
  [ContractGenerationQuestionType.NUMBER]: 'A number each document compares against.',
  [ContractGenerationQuestionType.DATE]: 'A date each document compares against.',
};

/**
 * How a document's comparison reads in a picker, per type — "under" and
 * "before" are the same operator and the same idea, but only one of them is
 * English about a date.
 */
const NUMBER_OPERATOR_LABEL: Record<ContractGenerationAnswerOperator, string> = {
  [ContractGenerationAnswerOperator.EQ]: 'is exactly',
  [ContractGenerationAnswerOperator.NE]: 'is anything but',
  [ContractGenerationAnswerOperator.LT]: 'is under',
  [ContractGenerationAnswerOperator.LTE]: 'is at most',
  [ContractGenerationAnswerOperator.GT]: 'is over',
  [ContractGenerationAnswerOperator.GTE]: 'is at least',
  [ContractGenerationAnswerOperator.BETWEEN]: 'is between',
};

const DATE_OPERATOR_LABEL: Record<ContractGenerationAnswerOperator, string> = {
  [ContractGenerationAnswerOperator.EQ]: 'is exactly',
  [ContractGenerationAnswerOperator.NE]: 'is anything but',
  [ContractGenerationAnswerOperator.LT]: 'is before',
  [ContractGenerationAnswerOperator.LTE]: 'is on or before',
  [ContractGenerationAnswerOperator.GT]: 'is after',
  [ContractGenerationAnswerOperator.GTE]: 'is on or after',
  [ContractGenerationAnswerOperator.BETWEEN]: 'is between',
};

export function operatorLabel(
  type: ContractGenerationQuestionType,
  operator: ContractGenerationAnswerOperator
): string {
  return type === ContractGenerationQuestionType.DATE
    ? DATE_OPERATOR_LABEL[operator]
    : NUMBER_OPERATOR_LABEL[operator];
}

/** The operators offered, in the order they read most naturally. */
export const ANSWER_OPERATORS: ContractGenerationAnswerOperator[] = [
  ContractGenerationAnswerOperator.EQ,
  ContractGenerationAnswerOperator.NE,
  ContractGenerationAnswerOperator.LT,
  ContractGenerationAnswerOperator.LTE,
  ContractGenerationAnswerOperator.GT,
  ContractGenerationAnswerOperator.GTE,
  ContractGenerationAnswerOperator.BETWEEN,
];

/**
 * Decimal operands arrive as strings, because that is what the backend's
 * `Decimal` generates. They are formatted for reading and never parsed back
 * for arithmetic — an operand goes to the wire exactly as it was typed.
 */
const NUMBER_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 20 });

export function formatNumberBound(value: string): string {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? NUMBER_FORMAT.format(parsed) : value;
}

/**
 * A comparison in words.
 *
 * `between` is half-open — `[value, value_max)` — and the wording has to carry
 * that. `[0, 50000)` and `[50000, ∞)` meet exactly once, so writing them as
 * "0–50,000" and "50,000–100,000" would put 50,000 in both as far as anyone
 * reading is concerned. "to under" leaves no such seam.
 */
function describeComparison(
  operator: ContractGenerationAnswerOperator,
  value: string,
  valueMax: string | null,
  format: (raw: string) => string,
  isDate: boolean
): string {
  const shown = format(value);
  switch (operator) {
    case ContractGenerationAnswerOperator.EQ: {
      return shown;
    }
    case ContractGenerationAnswerOperator.NE: {
      return `not ${shown}`;
    }
    case ContractGenerationAnswerOperator.LT: {
      return isDate ? `before ${shown}` : `under ${shown}`;
    }
    case ContractGenerationAnswerOperator.LTE: {
      return isDate ? `on or before ${shown}` : `${shown} or less`;
    }
    case ContractGenerationAnswerOperator.GT: {
      return isDate ? `after ${shown}` : `over ${shown}`;
    }
    case ContractGenerationAnswerOperator.GTE: {
      return isDate ? `from ${shown}` : `${shown} and above`;
    }
    case ContractGenerationAnswerOperator.BETWEEN: {
      if (valueMax === null) return shown;
      return `${shown} to ${isDate ? 'before' : 'under'} ${format(valueMax)}`;
    }
  }
}

/** What one document's answer stands for, in words. */
export function describeAnswer(answer: ContractGenerationVariantAnswerInDb): string {
  if (answer.question_type === ContractGenerationQuestionType.SELECTION) {
    return answer.value ?? 'any';
  }
  const isDate = answer.question_type === ContractGenerationQuestionType.DATE;
  const operator = answer.operator ?? null;
  const value = isDate ? (answer.date_value ?? null) : (answer.number_value ?? null);
  const valueMax = isDate ? (answer.date_value_max ?? null) : (answer.number_value_max ?? null);
  if (operator === null || value === null) return 'any';
  return describeComparison(
    operator,
    value,
    valueMax,
    isDate ? formatDateInputDisplay : formatNumberBound,
    isDate
  );
}

/** One document's answer to one question; null when it doesn't constrain it. */
export function answerFor(
  variant: ContractGenerationVariantAnswersSummary,
  questionUid: string
): ContractGenerationVariantAnswerInDb | null {
  return (variant.answers ?? []).find(answer => answer.question_uid === questionUid) ?? null;
}

/**
 * What one document actually narrows on, in the order the questions are asked.
 *
 * Questions it doesn't answer are left out rather than listed as "any". An
 * unanswered question is the widening kind of silence, and a row of "any" chips
 * reads like information while carrying none — worse, it crowds out the answers
 * that do mean something. A document constraining nothing comes back empty,
 * which the caller says in one line instead of N.
 */
export function describeConstraints(
  variant: ContractGenerationVariantAnswersSummary,
  questions: ContractGenerationTemplateQuestionInDb[]
): { question: string; value: string }[] {
  const entries: { question: string; value: string }[] = [];
  for (const question of questions) {
    const answer = answerFor(variant, question.uid);
    if (answer === null) continue;
    entries.push({ question: question.label, value: describeAnswer(answer) });
  }
  return entries;
}

/** The required questions this document still owes an answer to. */
export function missingRequired(
  variant: ContractGenerationVariantAnswersSummary,
  questions: ContractGenerationTemplateQuestionInDb[]
): ContractGenerationTemplateQuestionInDb[] {
  const owed = new Set(variant.unanswered_required_question_uids ?? []);
  return questions.filter(question => owed.has(question.uid));
}

/**
 * The answers a choice question has been given so far — derived, never
 * authored. Nobody states them when the question is created: a document
 * answers with free text and this is the distinct set of what they have said,
 * which is exactly what a picker offers.
 */
export function knownValues(question: ContractGenerationTemplateQuestionInDb): string[] {
  return question.values ?? [];
}

/**
 * The range a question is about. Nulls mean unbounded at that end, which is the
 * ordinary case — a form treats these as limits on what may be typed, never as
 * something that must be filled in.
 *
 * One pair whatever the type, because "the range this is about" is one idea.
 * The wire carries two pairs side by side — a number one and a date one — since
 * a request body cannot vary its fields by a sibling's value; that flattening
 * belongs at the edge, in `boundsPayload`, and nowhere else.
 */
export interface QuestionBounds {
  min: string | null;
  max: string | null;
}

/**
 * The range a question declares. Only an ordered question has one; a choice
 * question's admissible answers are the words its documents have used, which is
 * a different idea entirely.
 */
export function questionBounds(question: ContractGenerationTemplateQuestionInDb): QuestionBounds {
  switch (question.question_type) {
    case ContractGenerationQuestionType.NUMBER: {
      return { min: question.number_min ?? null, max: question.number_max ?? null };
    }
    case ContractGenerationQuestionType.DATE: {
      return { min: question.date_min ?? null, max: question.date_max ?? null };
    }
    case ContractGenerationQuestionType.SELECTION: {
      return { min: null, max: null };
    }
  }
}

/**
 * The create request's boundary fields for one question type — the inverse of
 * `questionBounds`, and the only place the wire's flat quadruple is assembled.
 *
 * Only the pair matching the type is ever sent: the backend refuses a bound of
 * the wrong kind rather than dropping it, so a date bound on a number question
 * is a 422 and not a silently missing limit. Making that choice here means no
 * call site can get the pairing wrong.
 */
export function boundsPayload(
  type: ContractGenerationQuestionType,
  bounds: QuestionBounds | null
): Pick<
  ContractGenerationTemplateQuestionCreateRequest,
  'number_min' | 'number_max' | 'date_min' | 'date_max'
> {
  if (bounds === null) return {};
  switch (type) {
    case ContractGenerationQuestionType.NUMBER: {
      return {
        ...(bounds.min === null ? {} : { number_min: bounds.min }),
        ...(bounds.max === null ? {} : { number_max: bounds.max }),
      };
    }
    case ContractGenerationQuestionType.DATE: {
      return {
        ...(bounds.min === null ? {} : { date_min: bounds.min }),
        ...(bounds.max === null ? {} : { date_max: bounds.max }),
      };
    }
    case ContractGenerationQuestionType.SELECTION: {
      return {};
    }
  }
}

/** The declared range in words, or null when the question is unbounded. */
export function describeBounds(question: ContractGenerationTemplateQuestionInDb): string | null {
  const { min, max } = questionBounds(question);
  if (min === null && max === null) return null;
  const format =
    question.question_type === ContractGenerationQuestionType.DATE
      ? formatDateInputDisplay
      : formatNumberBound;
  if (min !== null && max !== null) return `${format(min)} to ${format(max)}`;
  return min === null ? `${format(max as string)} and below` : `${format(min)} and above`;
}

/**
 * Whether a value falls inside the question's declared range.
 *
 * **Inclusive at both ends**, unlike the `between` operator on an answer. A
 * boundary names the values the question is *about*, so naming one has to admit
 * it; a band names the values a document *serves*, so adjacent bands must meet
 * exactly once. The two rules look alike and mean different things, so they are
 * kept in separate functions here for the same reason they are on the server.
 */
export function withinBounds(
  question: ContractGenerationTemplateQuestionInDb,
  value: string
): boolean {
  const { min, max } = questionBounds(question);
  if (min === null && max === null) return true;
  if (question.question_type === ContractGenerationQuestionType.DATE) {
    // ISO calendar dates compare correctly as text.
    if (min !== null && value < min) return false;
    return !(max !== null && value > max);
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return true; // "not a number" is its own message
  if (min !== null && numeric < Number(min)) return false;
  return !(max !== null && numeric > Number(max));
}

// ──────────────────────────────
// Editing what a document answers
//
// A document's answer is a comparison — a word it serves, or an operator and
// one or two operands. Shared between the two places answers are set: the
// editor on an existing document, and the dialog that adds a new one.
// ──────────────────────────────

/** One question's row in an answer form. Empty throughout means unanswered. */
export interface VariantAnswerDraft {
  /** Choice questions: the word this document serves. */
  value: string;
  /** Number and date questions: how it compares. Null until chosen. */
  operator: ContractGenerationAnswerOperator | null;
  /** Kept as typed: these are decimals, and a float round-trip would move them. */
  numberValue: string;
  numberValueMax: string;
  dateValue: string | null;
  dateValueMax: string | null;
}

export type VariantAnswerDraftMap = Record<string, VariantAnswerDraft | undefined>;

export function blankAnswerDraft(): VariantAnswerDraft {
  return {
    value: '',
    operator: null,
    numberValue: '',
    numberValueMax: '',
    dateValue: null,
    dateValueMax: null,
  };
}

/** A form with every question blank — a document that answers nothing yet. */
export function emptyAnswerDraft(
  questions: ContractGenerationTemplateQuestionInDb[]
): VariantAnswerDraftMap {
  return Object.fromEntries(questions.map(question => [question.uid, blankAnswerDraft()]));
}

/** A form opened on what a document answers today. */
export function answerDraftFor(
  variant: ContractGenerationVariantAnswersSummary,
  questions: ContractGenerationTemplateQuestionInDb[]
): VariantAnswerDraftMap {
  return Object.fromEntries(
    questions.map(question => {
      const answer = answerFor(variant, question.uid);
      return [
        question.uid,
        {
          value: answer?.value ?? '',
          operator: answer?.operator ?? null,
          numberValue: answer?.number_value ?? '',
          numberValueMax: answer?.number_value_max ?? '',
          dateValue: answer?.date_value ?? null,
          dateValueMax: answer?.date_value_max ?? null,
        },
      ];
    })
  );
}

function isNumeric(value: string): boolean {
  return value.trim().length > 0 && Number.isFinite(Number(value.trim()));
}

/** Whether a second operand applies — `between` is the only operator taking one. */
export function takesSecondOperand(operator: ContractGenerationAnswerOperator | null): boolean {
  return operator === ContractGenerationAnswerOperator.BETWEEN;
}

/**
 * Whether this question is answered at all — an empty row means "any".
 *
 * A half-made comparison counts as answered rather than blank: an operator with
 * no operand is something the author started, and reporting it as an error is
 * more use than silently dropping it on save.
 */
export function isAnswerDraftFilled(
  question: ContractGenerationTemplateQuestionInDb,
  row: VariantAnswerDraft
): boolean {
  switch (question.question_type) {
    case ContractGenerationQuestionType.SELECTION: {
      return row.value.trim().length > 0;
    }
    case ContractGenerationQuestionType.NUMBER: {
      return row.operator !== null || row.numberValue.trim().length > 0;
    }
    case ContractGenerationQuestionType.DATE: {
      return row.operator !== null || row.dateValue !== null;
    }
  }
}

/** What's wrong with one row, in the author's words; null when it's fine. */
export function answerDraftError(
  question: ContractGenerationTemplateQuestionInDb,
  row: VariantAnswerDraft
): string | null {
  if (question.question_type === ContractGenerationQuestionType.SELECTION) return null;
  if (!isAnswerDraftFilled(question, row)) return null;

  const isDate = question.question_type === ContractGenerationQuestionType.DATE;
  if (row.operator === null) return 'Pick how this document compares.';

  const covers = describeBounds(question);

  if (isDate) {
    if (row.dateValue === null) return 'Give a date to compare against.';
    if (takesSecondOperand(row.operator)) {
      if (row.dateValueMax === null) return 'Between needs a second date too.';
      if (row.dateValue >= row.dateValueMax) {
        return 'The first date has to fall before the second.';
      }
      if (!withinBounds(question, row.dateValueMax)) {
        return `This question only covers ${covers}.`;
      }
    }
    if (!withinBounds(question, row.dateValue)) return `This question only covers ${covers}.`;
    return null;
  }

  const value = row.numberValue.trim();
  if (value.length === 0) return 'Give a number to compare against.';
  if (!isNumeric(value)) return 'That isn’t a number.';
  if (takesSecondOperand(row.operator)) {
    const max = row.numberValueMax.trim();
    if (max.length === 0) return 'Between needs a second number too.';
    if (!isNumeric(max)) return 'The second value isn’t a number.';
    if (Number(value) >= Number(max)) return 'The first value has to be below the second.';
    if (!withinBounds(question, max)) return `This question only covers ${covers}.`;
  }
  if (!withinBounds(question, value)) return `This question only covers ${covers}.`;
  return null;
}

/**
 * How the comparison being typed will read once saved — the same words the
 * document card and the match picker use for it.
 *
 * Echoing it back as it is entered makes a misreading visible the moment it
 * happens, rather than at drafting time when a document quietly fails to match.
 * It earns its place most on `between`, whose upper operand is excluded.
 */
export function describeAnswerDraft(
  question: ContractGenerationTemplateQuestionInDb,
  row: VariantAnswerDraft
): string | null {
  if (!isAnswerDraftFilled(question, row) || answerDraftError(question, row) !== null) return null;
  if (question.question_type === ContractGenerationQuestionType.SELECTION) return null;
  if (row.operator === null) return null;

  const isDate = question.question_type === ContractGenerationQuestionType.DATE;
  const value = isDate ? row.dateValue : row.numberValue.trim();
  if (value === null || value.length === 0) return null;
  const valueMax = takesSecondOperand(row.operator)
    ? isDate
      ? row.dateValueMax
      : row.numberValueMax.trim() || null
    : null;
  return describeComparison(
    row.operator,
    value,
    valueMax,
    isDate ? formatDateInputDisplay : formatNumberBound,
    isDate
  );
}

/**
 * The complete answer set for the wire: only the questions actually answered.
 * A question left out stops constraining the document, which is the only way to
 * say "this one doesn't care about that question".
 *
 * A second operand travels only with `between` — sent alongside any other
 * operator the backend refuses the write rather than ignoring it, so that an
 * author who thought they had written a band is told they hadn't.
 */
export function toVariantAnswerPayload(
  questions: ContractGenerationTemplateQuestionInDb[],
  draft: VariantAnswerDraftMap
): ContractGenerationVariantAnswerUpdate[] {
  const entries: ContractGenerationVariantAnswerUpdate[] = [];
  for (const question of questions) {
    const row = draft[question.uid] ?? blankAnswerDraft();
    if (!isAnswerDraftFilled(question, row)) continue;
    if (answerDraftError(question, row) !== null) continue;

    switch (question.question_type) {
      case ContractGenerationQuestionType.SELECTION: {
        entries.push({ question_uid: question.uid, value: row.value.trim() });
        break;
      }
      case ContractGenerationQuestionType.NUMBER: {
        entries.push({
          question_uid: question.uid,
          operator: row.operator,
          number_value: row.numberValue.trim(),
          ...(takesSecondOperand(row.operator)
            ? { number_value_max: row.numberValueMax.trim() }
            : {}),
        });
        break;
      }
      case ContractGenerationQuestionType.DATE: {
        entries.push({
          question_uid: question.uid,
          operator: row.operator,
          date_value: row.dateValue,
          ...(takesSecondOperand(row.operator) ? { date_value_max: row.dateValueMax } : {}),
        });
        break;
      }
    }
  }
  return entries;
}

/**
 * The drafting user's typed values as the match endpoint wants them: one entry
 * per answered question, carrying a plain value and no operator — each document
 * decides for itself what it accepts.
 *
 * A blank answer is left out entirely rather than sent as null: the backend
 * reads absence as "don't filter on this", which is the widening kind of
 * silence, and a null would be saying something else.
 */
export function toMatchAnswers(
  questions: ContractGenerationTemplateQuestionInDb[],
  // Typed as possibly-absent: a question nobody answered has no key at all.
  answers: Record<string, string | undefined>
): ContractGenerationTemplateMatchAnswer[] {
  const entries: ContractGenerationTemplateMatchAnswer[] = [];
  for (const question of questions) {
    const value = answers[question.uid];
    if (value === undefined || value.trim().length === 0) continue;
    switch (question.question_type) {
      case ContractGenerationQuestionType.SELECTION: {
        entries.push({ question_uid: question.uid, value: value.trim() });
        break;
      }
      case ContractGenerationQuestionType.NUMBER: {
        // A number goes up as the text that was typed: these are decimals
        // server-side, and a float round-trip could move the value.
        if (isNumeric(value)) {
          entries.push({ question_uid: question.uid, number_value: value.trim() });
        }
        break;
      }
      case ContractGenerationQuestionType.DATE: {
        entries.push({ question_uid: question.uid, date_value: value });
        break;
      }
    }
  }
  return entries;
}
